import {generateGuid} from '@packages/utilities/guid';
import {getFirst, getSecond, pair} from '@packages/utilities/Pair';
import {weakMemo} from '@packages/utilities/memo';
import {reportBug, reportBugOnce} from '@packages/utilities/reporter';
import {
  Plugins,
  Component,
  Expressions,
  matchExpression,
  Type,
} from '@packages/systems/wfdl';
import {
  EElement,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  getName,
  getPluginName,
  qualify,
  QualifiedMap,
  MutableQualifiedMap,
  type Qualified,
  type PluginName,
} from '@packages/systems/wfdl-Qualified';
import {type State} from '@packages/utilities/State';
import {isUserComponent} from '@packages/utilities/core-elements';
import {
  instantiateExpressionWithUniqueIds,
  instantiateExpressionWithUniqueIdsState,
  type UniqueIdsState,
} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {
  getSlotDefaultLists,
  mapRenderAndSlotDefaultLists,
  mapSlotDefaultLists,
} from '@packages/utilities/platform-slots';
import {isComponentDisplayNameTaken} from './ComponentMapUtils';
import {createConflictResolver} from '@packages/utilities/string-utils';
import {identity, constant, concat} from '@packages/utilities/fp/utils';
import {Map as ImmMap} from 'immutable';
import {
  remapAssetIdsInComponent,
  updateStyleVariants,
} from '@packages/systems/libraries';
import {remapOverlayTargetReferences} from '@packages/systems/overlays';
import {preserveClickGoalBindings} from './preserveClickGoalBindings';

/**
 * Component `origin` type
 */
type ComponentOrigin = Qualified<Component.ComponentName>;

/**
 * Map of component origin types to their local copy's type.
 */
type ComponentNameMap = QualifiedMap<
  Component.ComponentName,
  Qualified<Component.ComponentName>
>;
/**
 * Mutable ComponentNameMap - only used internally for performance optimization
 */
type MutableComponentNameMap = MutableQualifiedMap<
  Component.ComponentName,
  Qualified<Component.ComponentName>
>;
/**
 * Mutable ComponentMap - only used internally for performance optimization
 */
type MutableComponentMap = MutableQualifiedMap<
  Component.ComponentName,
  Component.ComponentType
>;

export type UpdateComponentRenderFnType = (params: {
  render: Expression;
  includesSlotDefaults?: boolean;
  dataType: Type;
  origin: Qualified<string>;
  variants: Component.Variants | undefined;
  /**
   * If true it indicates we're updating a component render
   * that needs to be detached from the origin (library)
   */
  detachFromOrigin?: boolean;
}) => Expression;

/**
 * A map of old ids to new ids that is used in many places to update
 * various data structures: component render, dataType, style blocks.
 *
 * This might contain:
 *  - element ids
 *  - style block ids
 *  - asset ids
 */
type IdMap = {[id: string]: string};

/**
 * A map of orphaned components by Plugin ID
 */
type OrphanedComponentsMap = {
  [pluginId: string]: Qualified<Component.ComponentName>[];
};

/**
 * Parses all User Components with an origin set
 * and builds a QualifedMap with the origin type as key
 * and the User Component type as value
 */
const buildComponentOriginMap = weakMemo(
  (componentMap: Component.ComponentMap): ComponentNameMap => {
    const originMap_mut = new MutableQualifiedMap() satisfies ComponentNameMap;

    componentMap.forEach((component, name) => {
      if (isUserComponent(name)) {
        const origin = Component.getOrigin(component);
        if (origin) {
          // There was a bug with Basic::LineBreak plugin components
          // being treated as library components. The bug has been fixed
          // but we still need to run a migration, so there are production sites
          // with LineBreak components with an origin. We should exclude them from here.
          // Migration ticket: https://webflow.atlassian.net/browse/SCALE-959
          if (originMap_mut.has(origin) && getPluginName(origin) !== 'Basic') {
            // If there is already an entry it means we have multiple
            // components with the same origin
            reportError(
              `[buildComponentOriginMap] Duplicate origin found ${origin}`,
              {
                reportOnce: true,
              }
            );
          }

          originMap_mut.set_mut(origin, name);
        }
      }
    });

    return originMap_mut.asNonMutableCopy();
  }
);

type ComponentMapByOriginPlugin = {
  [pluginName: string]: MutableQualifiedMap<
    Component.ComponentName,
    Component.ComponentType
  >;
};

/**
 * Returns a subset of the component map that contains only components from the given origin plugin
 */
export const getComponentMapForOrigin = weakMemo(
  (componentMap: Component.ComponentMap) => {
    const componentMapByOriginPlugin: ComponentMapByOriginPlugin =
      componentMap.reduce((result, component, name) => {
        if (isUserComponent(name)) {
          const origin = Component.getOrigin(component);
          if (origin) {
            const pluginName = getPluginName(origin);
            if (!result[pluginName]) {
              result[pluginName] =
                new MutableQualifiedMap() satisfies Component.ComponentMap;
            }
            result[pluginName].set_mut(name, component);
          }
        }
        return result;
      }, {} as ComponentMapByOriginPlugin);

    return (originPluginName: string) => {
      return componentMapByOriginPlugin[originPluginName]
        ? componentMapByOriginPlugin[originPluginName].asNonMutableCopy()
        : (new QualifiedMap() satisfies Component.ComponentMap);
    };
  }
);

const avoidDisplayNameConflict = createConflictResolver(identity);

type ReplaceInstState = {
  plugins: Plugins.PluginsType;
  componentOriginMap_mut: MutableComponentNameMap;
  componentOriginMap_changed: boolean;
};

const _replacePluginComponentInstInExpression: (
  expression: Expression
) => State<ReplaceInstState, Expression> = Expressions.everywhereWithState(
  matchExpression({
    Element: (el) => (state) => {
      // If not plugin component, do nothing
      if (!Plugins.isPluginComponentInst(state.plugins, el)) {
        return pair(state, EElement(el));
      }

      // If plugin component from plugin with
      // import strategy !== 'VALUE', do nothing
      if (
        Plugins.getPluginImportStrategy(
          state.plugins,
          getPluginName(el.type)
        ) !== Plugins.IMPORT_STRATEGY.VALUE
      ) {
        return pair(state, EElement(el));
      }

      // Lookup if component already exists
      const localComponentType = state.componentOriginMap_mut.get(el.type);

      let componentType: Qualified<Component.ComponentName>;

      if (localComponentType) {
        // If exists, use existing name and don't update componentOriginMap
        componentType = localComponentType;
      } else {
        // If not found, create new name and update componentOriginMap + set 'changed' flag
        componentType = qualify(
          Plugins.name('__SitePlugin'),
          Component.name(generateGuid())
        );
        state.componentOriginMap_mut.set_mut(el.type, componentType);
        state.componentOriginMap_changed = true;
      }

      return pair(
        state,
        EElement({
          ...el,
          type: componentType,
        })
      );
    },
    _: (expr) => (state: ReplaceInstState) => pair(state, expr),
  })
);

/**
 * Replace all Plugin Component instances in a given Expression
 * with corresponding __SitePlugin Components. Updates the given ComponentNameMap
 * with new entries if __SitePlugin Components doesn't exist yet.
 */
const replacePluginComponentInstInExpression = (
  expr: Expression,
  componentOriginMap_mut: MutableComponentNameMap,
  plugins: Plugins.PluginsType
): {
  elementExpression: Expression;
  componentOriginMap_changed: boolean;
} => {
  const initialState = {
    plugins,
    componentOriginMap_mut,
    componentOriginMap_changed: false,
  };

  const result = _replacePluginComponentInstInExpression(expr)(initialState);
  return {
    elementExpression: getSecond(result),
    componentOriginMap_changed: getFirst(result).componentOriginMap_changed,
  };
};

const _replaceUserComponentInstInExpression: (expression: Expression) => State<
  {
    componentNameMap: ComponentNameMap;
    changed: boolean;
  },
  Expression
> = Expressions.everywhereWithState(
  matchExpression({
    Element: (el) => (state) => {
      // If not a user component, do nothing
      if (!isUserComponent(el.type)) {
        return pair(state, EElement(el));
      }

      // If not a component instance to remap, do nothing
      const remapComponentType = state.componentNameMap.get(el.type);
      if (!remapComponentType) {
        return pair(state, EElement(el));
      }

      state.changed = true;

      return pair(
        state,
        EElement({
          ...el,
          type: remapComponentType,
        })
      );
    },
    _: (expr) => (state) => pair(state, expr),
  })
);

/**
 * Given a ComponentNameMap, replace all User Component instances
 * in a given Expression with the corresponding Components.
 */
const replaceUserComponentInstInExpression = (
  expr: Expression,
  componentNameMap: ComponentNameMap
): {
  elementExpression: Expression;
  changed: boolean;
} => {
  const initialState = {
    componentNameMap,
    changed: false,
  };

  const result = _replaceUserComponentInstInExpression(expr)(initialState);
  return {
    elementExpression: getSecond(result),
    changed: getFirst(result).changed,
  };
};

/**
 * - Iterates over `componentOriginMap` (qualified map of [origin] => [local component name])
 * - If [origin] component not found: report bug
 * - If [local component name] already existing in `componentMap`, early return
 * - Else, creates a new [local component name] component by copying [origin] component
 *   and adding the `origin` field
 * - New [local component name] component render is:
 *   - instantiated with unique ids
 *   - updated to replace nested [origin] instances (plugin components) with respective
 *     local components (using `componentOriginMap` to lookup [local component name])
 *     - if [local component name] not found, updates `componentOriginMap` with [origin] => [_new_ local component name]
 *   - updated with `updateComponentRenderFn`
 *   - updated with asset IDs found in state using `idMap`
 * - If `componentOriginMap` was updated, calls `reduceComponentOriginMap` recursively
 *
 * Returns metadata with lists of component origins that have been created
 */
const reduceComponentOriginMap = (
  componentOriginMap_mut: MutableComponentNameMap,
  componentMap_mut: MutableComponentMap,
  idMap_mut: Map<string, string>,
  plugins: Plugins.PluginsType,
  componentsWithMissingStyleVariants_mut: Map<
    string,
    {originType: Qualified<string>; pluginName: string}
  >,
  // Custom update render function
  updateComponentRenderFn?: UpdateComponentRenderFnType
): {created: ComponentOrigin[]} => {
  const pluginComponents = Plugins.getPluginComponents(plugins);
  let nestedComponentsFound = false;
  const created: ComponentOrigin[] = [];

  // Parse the component origin map and create new Components if needed
  componentOriginMap_mut.forEach(
    (
      localComponentType: Qualified<Component.ComponentName>,
      originType: Qualified<Component.ComponentName>
    ) => {
      const component = pluginComponents.get(originType);
      if (!component) {
        // Plugin Component not found: we found an orphaned component
        reportError(
          `[reduceComponentOriginMap] Component type ${originType} not found`
        );
        return;
      }

      const userComponentExists = componentMap_mut.has(localComponentType);
      if (userComponentExists) {
        // User Component already exists: skip it
        return;
      }

      /*
       * When we create a new local version of the Origin Component, we must produce
       * new global unique IDs for each element in the `render` expression of the
       * new Local Component definition.
       */
      const componentName = getName(localComponentType);
      const result = instantiateExpressionWithUniqueIds(
        Component.getRender(component)
      )(componentName);
      const idMapState = getFirst(result);
      /*
       * Popover and Modal target references store the origin element ids of
       * the target and of its owning component, so they must follow the render
       * onto the freshly instantiated local ids.
       */
      const render = remapOverlayTargetReferences(
        getSecond(result),
        idMapState.idMap
      );
      const variants = Component.getVariants(component);

      // Update render to replace PC references
      const {elementExpression: updatedRender, componentOriginMap_changed} =
        replacePluginComponentInstInExpression(
          render,
          componentOriginMap_mut,
          plugins
        );

      // Slot default trees continue the render's id sequence so ids stay unique
      let slotIdState: UniqueIdsState = {id: idMapState.id, idMap: {}};
      let slotListsChanged = false;
      const slotDefaultsReplaced = mapSlotDefaultLists(component, (list) => {
        const listResult =
          instantiateExpressionWithUniqueIdsState(list)(slotIdState);
        slotIdState = getFirst(listResult);
        Object.assign(idMapState.idMap, slotIdState.idMap);
        const {elementExpression, componentOriginMap_changed: listChanged} =
          replacePluginComponentInstInExpression(
            remapOverlayTargetReferences(
              getSecond(listResult),
              idMapState.idMap
            ),
            componentOriginMap_mut,
            plugins
          );
        slotListsChanged = slotListsChanged || listChanged;
        return elementExpression;
      });

      // Update render and slot defaults with escape hatch updater fn
      const {render: nextRenderFromFn, component: withUpdatedSlotDefaults} =
        mapRenderAndSlotDefaultLists(
          slotDefaultsReplaced,
          updatedRender,
          (trees, includesSlotDefaults) =>
            typeof updateComponentRenderFn === 'function'
              ? updateComponentRenderFn({
                  render: trees,
                  includesSlotDefaults,
                  dataType: component.dataType,
                  origin: originType,
                  variants,
                })
              : trees
        );
      let nextRender = nextRenderFromFn;

      const currentIdMap = Object.fromEntries(idMap_mut.entries());

      // TODO: move here the updateXscpStyleBlockIds call to update
      // style block ids in component render
      // nextRender = updateXscpStyleBlockIds(currentIdMap)(nextRender);

      const nextComponent = remapAssetIdsInComponent(
        plugins,
        Component.update(withUpdatedSlotDefaults, {render: nextRender}),
        currentIdMap
      );
      nextRender = Component.getRender(nextComponent);
      const {updatedDataType, hasMissingStyleVariants} = updateStyleVariants(
        Component.getDataType(nextComponent),
        getPluginName(localComponentType),
        originType,
        componentOriginMap_mut
      );

      if (hasMissingStyleVariants) {
        componentsWithMissingStyleVariants_mut.set(componentName, {
          originType,
          pluginName: getPluginName(localComponentType),
        });
      }

      // Update with origin and new render
      const newComponent = Component.update(nextComponent, {
        render: nextRender,
        dataType: updatedDataType,
        origin: originType,
      });

      // Update mutable componentMap and idMap
      componentMap_mut.set_mut(localComponentType, newComponent);
      created.push(originType);

      /*
       * This is a mapping of Origin Component element ID -> Local Component element
       * ID, which is useful in cases where you need to update external data
       * structures that reference the Origin IDs.
       */
      Object.entries(idMapState.idMap).forEach(([key, value]) => {
        idMap_mut.set(key, value);
      });

      // Update flag
      nestedComponentsFound =
        nestedComponentsFound || componentOriginMap_changed || slotListsChanged;
    }
  );

  // If there are nested components,
  if (nestedComponentsFound) {
    const {created: createdNested} = reduceComponentOriginMap(
      componentOriginMap_mut,
      componentMap_mut,
      idMap_mut,
      plugins,
      componentsWithMissingStyleVariants_mut,
      updateComponentRenderFn
    );

    created.push(...createdNested);
  }

  return {created};
};

/**
 * Updates the style variant types for components that could not get their style variant types updated initially.
 * This can happen in cases where there are deeply nested components added for the first time on the consumer page.
 */
function updateMissingStyleVariants(
  componentOriginMap_mut: MutableComponentNameMap,
  componentMap_mut: MutableComponentMap,
  componentsWithMissingStyleVariants_mut: Map<
    string,
    {originType: Qualified<string>; pluginName: string}
  >
) {
  componentsWithMissingStyleVariants_mut.forEach(
    ({originType, pluginName}, componentName) => {
      const component = componentMap_mut.get(
        qualify('__SitePlugin', componentName)
      );
      if (!component) {
        return;
      }
      const {updatedDataType} = updateStyleVariants(
        component.dataType,
        pluginName,
        originType,
        componentOriginMap_mut
      );
      componentMap_mut.set_mut(
        qualify('__SitePlugin', componentName),
        Component.update(component, {
          dataType: updatedDataType,
        })
      );
    }
  );
}

const reportError = (
  message: string,
  opts?: {throwError?: boolean; reportOnce?: boolean}
) => {
  const {throwError = false, reportOnce = false} = opts || {};
  const reportFn = reportOnce ? reportBugOnce : reportBug;
  reportFn('ComponentOriginUtilsError', message, {
    team: 'structure',
    ticket: 'https://webflow.atlassian.net/browse/SCALE-142',
  });

  if (throwError) {
    throw new Error(`[ComponentOriginUtilsError] ${message}`);
  }
};

/**
 * Get a "local" component that originates from another component. In other
 * words, look up a component by its `origin` field.
 *
 * If no component with the supplied origin is found, returns `null`.
 */
export const getComponentByOrigin = (
  /**
   * The ID (qualified name) of the component from which the local component
   * we're looking for was derived from.
   */
  origin: Qualified<Component.ComponentName>,
  /**
   * The current map of local components.
   */
  componentMap: Component.ComponentMap
): {
  componentId: Qualified<Component.ComponentName> | null;
  component: Component.ComponentType | null;
} => {
  const componentId = buildComponentOriginMap(componentMap).get(origin);
  if (!componentId) {
    return {
      component: null,
      componentId: null,
    };
  }

  return {
    component: componentMap.get(componentId) || null,
    componentId,
  };
};

/**
 * Given a Component Instance element, get its root Component's origin.
 */
export const getComponentInstOrigin = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo(
      (
        element: ExpressionElement
      ): Qualified<Component.ComponentName> | null => {
        const componentRoot = componentMap.get(element.type);
        if (!componentRoot) {
          return null;
        }
        return Component.getOrigin(componentRoot) || null;
      }
    )
);

/**
 * Determines if a component from origin ElementPreset should be imported:
 * - ElementPreset import strategy is VALUE
 * - and the local component rendered by the element preset doesn't exist yet
 */
export const shouldImportComponentFromOrigin = (
  /**
   * The ID (qualified name) of the ElementPreset from which the local component
   * we're looking to import was derived from.
   */
  elementPreset: Plugins.QualifiedElementPreset,
  /**
   * The current map of local components.
   */
  componentMap: Component.ComponentMap,
  /**
   * The current plugins.
   */
  plugins: Plugins.PluginsType
) => {
  const importStrategy = Plugins.getElementPresetImportStrategy(
    plugins,
    elementPreset
  );
  if (importStrategy !== Plugins.IMPORT_STRATEGY.VALUE) {
    return true;
  }
  // Note that in Library Plugins the ElementPreset ID is the same
  // as the Component ID, so here we can use it as the local component origin.
  return getComponentByOrigin(elementPreset, componentMap).component === null;
};

/**
 * Given an element that may contain instances to components whose origin is
 * from outside the local site, replace all external component instances with
 * local component copies, creating new local components as necessary.
 */
export function replaceOriginComponentInstancesWithLocalComponents({
  plugins,
  componentMap,
  element,
  updateComponentRenderFn,
  idMap: initialIdMap = {},
}: {
  /**
   * Current plugins in state
   */
  plugins: Plugins.PluginsType;
  /**
   * Current component map
   */
  componentMap: Component.ComponentMap;
  /**
   * The element expression in which we want to replace component instances
   */
  element: ExpressionElement;
  /**
   * A map of old ids to new ids
   */
  idMap?: IdMap;
  /**
   * An escape hatch used to update the component render
   * with dependencies that are outside packages.
   * TODO: remove this once we move all things to packages
   */
  updateComponentRenderFn?: UpdateComponentRenderFnType;
}): {
  /**
   * The updated element expression to replace the input `element`
   * */
  element: ExpressionElement;
  /**
   * The updated `componentMap` containing added or changed components (if any).
   */
  componentMap: Component.ComponentMap;
  /**
   * The updated map of old ids to new ids
   */
  idMap: IdMap;
} {
  // Element must be a valid Plugin Component instance.
  if (!Plugins.isPluginComponentInst(plugins, element)) {
    // This can happen when importing Layouts from libraries
    return {
      element,
      componentMap,
      idMap: initialIdMap,
    };
  }

  const componentOriginMap = buildComponentOriginMap(componentMap);
  const componentOriginMap_mut = componentOriginMap.asMutableCopy();

  // Replace Plugin Components with User Components in element expression
  const {elementExpression: updatedExpression, componentOriginMap_changed} =
    replacePluginComponentInstInExpression(
      EElement(element),
      componentOriginMap_mut,
      plugins
    );

  const updatedElement = Expressions.getElement(updatedExpression);
  if (!updatedElement) {
    reportError(
      '[replaceOriginComponentInstancesWithLocalComponents] Unable to replace Plugin Component instance in Expression',
      {
        throwError: true,
      }
    );
    return {
      element,
      componentMap,
      idMap: initialIdMap,
    };
  }

  // If no new Components were found, we return the updated element
  if (!componentOriginMap_changed) {
    return {
      element: updatedElement,
      componentMap,
      idMap: initialIdMap,
    };
  }

  // Otherwise we create the new "local" User Components and update the ComponentMap
  const componentMap_mut = componentMap.asMutableCopy();
  const idMap_mut = new Map<string, string>(Object.entries(initialIdMap));
  const componentsWithMissingStyleVariants_mut = new Map<
    string,
    {originType: Qualified<string>; pluginName: string}
  >();

  reduceComponentOriginMap(
    componentOriginMap_mut,
    componentMap_mut,
    idMap_mut,
    plugins,
    componentsWithMissingStyleVariants_mut,
    updateComponentRenderFn
  );

  updateMissingStyleVariants(
    componentOriginMap_mut,
    componentMap_mut,
    componentsWithMissingStyleVariants_mut
  );

  return {
    element: updatedElement,
    componentMap: componentMap_mut.asNonMutableCopy(),
    idMap: Object.fromEntries(idMap_mut.entries()),
  };
}

/**
 * Returns an updated `componentMap` where existing "local" user components
 * that originated from the `originPluginName` are replaced with
 * the _new_ components from `newPluginName` (maintaining the same name).
 *
 * New "local" user components are created if needed when encountering nested
 * components that weren't yet imported as "local" components.
 *
 * "Local" user components whose origin doesn't exist in the new plugin
 * are "detached" by removing the `origin` field (and become "regular" user components).
 */
export function replaceOriginComponentsWithNewPlugin({
  originPluginName,
  newPluginName,
  plugins,
  componentMap,
  updateComponentRenderFn,
  originComponentsToDetach = new Set<Component.ComponentName>(),
  preserveClickGoalBindingsFn = preserveClickGoalBindings,
}: {
  /**
   * The name of the plugin we're replacing
   */
  originPluginName: PluginName;
  /**
   * The name of the new plugin we're replacing the plugin with
   */
  newPluginName: PluginName;
  /**
   * Plugins, including the _new_ plugin
   */
  plugins: Plugins.PluginsType;
  /**
   * The current map of components
   */
  componentMap: Component.ComponentMap;
  /**
   * An optional updater function to update each component render
   */
  updateComponentRenderFn?: UpdateComponentRenderFnType;
  /**
   * A list of library components to "detach" from their corresponding local component
   */
  originComponentsToDetach?: Set<Component.ComponentName>;
  /**
   * Carries click goal bindings onto each rebuilt local copy. The server passes
   * a traced version.
   */
  preserveClickGoalBindingsFn?: typeof preserveClickGoalBindings;
}): {
  /**
   * The updated `componentMap` containing added or changed components (if any).
   */
  componentMap: Component.ComponentMap;
  /**
   * When we create a new local version of the Origin Component, we must produce
   * new global unique IDs for each element in the `render` expression of the
   * new Local Component definition.
   *
   * This is a mapping of Origin Component element ID -> Local Component element
   * ID, which is useful in cases where you need to update external data
   * structures that reference the Origin IDs.
   */
  idMap: {[key: string]: string};
  /**
   * A list of new component origins that have been added/updated to the `componentMap`
   * and need to be processed on the caller side. We use this to lookup the component
   * dependencies in the elementPreset from the plugin.
   */
  newComponents: ComponentOrigin[];
  /**
   * A list of local components that have been detached from the origin plugin
   * and need to be processed on the caller side.
   */
  detachedComponents: Qualified<Component.ComponentName>[];
} {
  const pluginComponents = Plugins.getPluginComponents(plugins);

  // We keep a list of components are being added/updated and detached
  const detachedComponents: Qualified<Component.ComponentName>[] = [];
  let newComponents: ComponentOrigin[] = [];

  // We build a `componentOriginMap` with the _new_ plugin origins
  // mapped to the _existing_ local component names
  const componentOriginMap_mut =
    new MutableQualifiedMap() satisfies ComponentNameMap;
  // We build a componentMap with all the changed components
  const componentMapChanges_mut =
    new MutableQualifiedMap() satisfies Component.ComponentMap;
  const idMap_mut = new Map();
  const componentsWithMissingStyleVariants_mut = new Map<
    string,
    {originType: Qualified<string>; pluginName: string}
  >();

  // We use a flag to early return if no components need to be replaced
  let needsReplacement = false;

  buildComponentOriginMap(componentMap).forEach(
    (localComponentName, origin) => {
      // Evaluate only origins from the given `originPluginName`
      if (getPluginName(origin) !== originPluginName) {
        return;
      }
      needsReplacement = true;

      // Build the new origin by replacing the plugin name with `newPluginName`
      const newPluginOrigin = qualify(newPluginName, getName(origin));

      // Check if it should be imported by 'value" (with origin),
      // otherwise we detach it
      const shouldImportWithOrigin =
        Plugins.getElementPresetImportStrategy(plugins, newPluginOrigin) ===
        Plugins.IMPORT_STRATEGY.VALUE;

      const shouldDetach = originComponentsToDetach.has(getName(origin));
      const existsInNewPlugin = pluginComponents.has(newPluginOrigin);

      if (existsInNewPlugin && shouldImportWithOrigin && !shouldDetach) {
        // If it exists, add it to the `componentOriginMap`
        componentOriginMap_mut.set_mut(newPluginOrigin, localComponentName);
      } else {
        // Else, we detach the component
        const component = componentMap.get(localComponentName);
        if (!component) {
          // This should never happen
          reportError(
            `[replaceOriginComponentsWithNewPlugin] Component type ${localComponentName} not found`,
            {throwError: true}
          );
          return;
        }
        componentMapChanges_mut.set_mut(
          localComponentName,
          detachComponentFromOrigin(
            component,
            componentMap,
            updateComponentRenderFn
          )
        );
        detachedComponents.push(localComponentName);
      }
    }
  );

  if (!needsReplacement) {
    // If we didn't encounter components that need to be updated
    // then we can early return
    return {
      componentMap,
      idMap: {},
      newComponents,
      detachedComponents,
    };
  }

  /*
   * Here we iterate over `componentOriginMap_mut` and mutate
   * `componentMapChanges_mut` to include the new user components where:
   * - the component name is the existing user componentName
   * - the component content is the new plugin component from origin
   */
  ({created: newComponents} = reduceComponentOriginMap(
    componentOriginMap_mut,
    componentMapChanges_mut,
    idMap_mut,
    plugins,
    componentsWithMissingStyleVariants_mut,
    updateComponentRenderFn
  ));

  updateMissingStyleVariants(
    componentOriginMap_mut,
    componentMapChanges_mut,
    componentsWithMissingStyleVariants_mut
  );

  /*
   * Local copies are rebuilt from the new plugin, so click goal props and
   * bindings the site added to them must be carried over from the previous copy.
   */
  componentOriginMap_mut.forEach((localComponentName) => {
    const previousComponent = componentMap.get(localComponentName);
    const nextComponent = componentMapChanges_mut.get(localComponentName);
    if (!previousComponent || !nextComponent) {
      return;
    }
    const previousOrigin = Component.getOrigin(previousComponent);
    componentMapChanges_mut.set_mut(
      localComponentName,
      preserveClickGoalBindingsFn({
        componentName: getName(localComponentName),
        previousComponent,
        previousOriginComponent: previousOrigin
          ? pluginComponents.get(previousOrigin)
          : undefined,
        nextComponent,
        nextIdByOriginId: idMap_mut,
      })
    );
  });

  /*
   * Here `componentMapChanges_mut` has:
   * - all the local user components with content updated to the new plugin origin
   * - any new local user component that was created because found as nested component
   * - all the "detached" local user components (with no origin)
   *
   * By merging it into the existing `componentMap` we override all the previous
   * local components that referred to the old origin plugin.
   */
  const nextComponentMap = componentMap.union(
    componentMapChanges_mut.asNonMutableCopy()
  );

  return {
    componentMap: nextComponentMap,
    idMap: Object.fromEntries(idMap_mut.entries()),
    newComponents,
    detachedComponents,
  };
}

/**
 * Returns an updated `componentMap` where existing "local" user components
 * that originated from the `pluginName` are "detached" by removing the `origin`
 * field (and become "regular" user components).
 */
export function detachAllComponentsFromPlugin({
  pluginName,
  componentMap,
  updateComponentRenderFn,
}: {
  /**
   * The name of the plugin we're detaching components from
   */
  pluginName: PluginName;
  /**
   * The current map of components
   */
  componentMap: Component.ComponentMap;
  /**
   * An optional updater function to update each component render
   */
  updateComponentRenderFn?: UpdateComponentRenderFnType;
}): {
  /**
   * The updated `componentMap` containing changed components (if any).
   */
  componentMap: Component.ComponentMap;
  /**
   * When we create a new local version of the Origin Component, we must produce
   * new global unique IDs for each element in the `render` expression of the
   * new Local Component definition.
   *
   * This is a mapping of Origin Component element ID -> Local Component element
   * ID, which is useful in cases where you need to update external data
   * structures that reference the Origin IDs.
   */
  idMap: {[key: string]: string};
  /**
   * A list of local components that have been detached from the origin plugin
   * and need to be processed on the caller side.
   */
  detachedComponents: Qualified<Component.ComponentName>[];
} {
  // We keep a list of components are being detached
  const detachedComponents: Qualified<Component.ComponentName>[] = [];

  // We build a componentMap with all the changed components
  const componentMapChanges_mut =
    new MutableQualifiedMap() satisfies Component.ComponentMap;
  const idMap_mut = new Map();

  // We use a flag to early return if no components need to be replaced
  let needsReplacement = false;

  buildComponentOriginMap(componentMap).forEach(
    (localComponentName, origin) => {
      // Evaluate only origins from the given `pluginName`
      if (getPluginName(origin) !== pluginName) {
        return;
      }
      needsReplacement = true;

      // We detach the component
      const component = componentMap.get(localComponentName);
      if (!component) {
        // This should never happen
        reportError(
          `[replaceOriginComponentsWithNewPlugin] Component type ${localComponentName} not found`,
          {throwError: true}
        );
        return;
      }
      componentMapChanges_mut.set_mut(
        localComponentName,
        detachComponentFromOrigin(
          component,
          componentMap,
          updateComponentRenderFn
        )
      );
      detachedComponents.push(localComponentName);
    }
  );

  if (!needsReplacement) {
    // If we didn't encounter components that need to be updated
    // then we can early return
    return {
      componentMap,
      idMap: {},
      detachedComponents,
    };
  }

  /*
   * Here `componentMapChanges_mut` has:
   * - all the "detached" local user components (with no origin)
   *
   * By merging it into the existing `componentMap` we override all the previous
   * local components that referred to the uninstalled origin plugin.
   */
  const nextComponentMap = componentMap.union(
    componentMapChanges_mut.asNonMutableCopy()
  );

  return {
    componentMap: nextComponentMap,
    idMap: Object.fromEntries(idMap_mut.entries()),
    detachedComponents,
  };
}

/**
 * "Detaches" a component from its origin (library plugin)
 * 1. unset the `origin`
 * 2. update `displayName` to avoid conflicts with existing components
 * 3. update `render` with updateComponentRenderFn which updates all the
 *    references to different objects ids like styleBlocks and ix2
 */
export const detachComponentFromOrigin = (
  component: Component.ComponentType,
  componentMap: Component.ComponentMap,
  updateComponentRenderFn?: UpdateComponentRenderFnType
): Component.ComponentType => {
  const origin = Component.getOrigin(component);
  if (!origin) {
    // Already detached - this should never be possible
    reportError('Trying to detach a component that was already detached');
    return component;
  }
  const detachFromOrigin = true;
  const render = Component.getRender(component);
  const variants = Component.getVariants(component);
  const {render: nextRender, component: withSlotDefaults} =
    mapRenderAndSlotDefaultLists(
      component,
      render,
      (trees, includesSlotDefaults) =>
        updateComponentRenderFn
          ? updateComponentRenderFn({
              render: trees,
              includesSlotDefaults,
              dataType: component.dataType,
              origin,
              detachFromOrigin,
              variants,
            })
          : trees
    );

  const displayName = Component.getDisplayName(component);
  const nextDisplayName = isComponentDisplayNameTaken(displayName, componentMap)
    ? getDisplayNameWithNoConflicts(displayName, componentMap)
    : displayName;

  return Component.update(withSlotDefaults, {
    displayName: nextDisplayName,
    render: nextRender,
    origin: undefined,
  });
};

/**
 * Parses the componentMap and returns a list of components
 * whose origin doesn't exist in the plugin components
 */
export const getOrphanedComponentsByPlugin = (
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): OrphanedComponentsMap => {
  const orphanedComponents: OrphanedComponentsMap = {};
  const pluginComponents = Plugins.getPluginComponents(plugins);

  componentMap.forEach((component, componentType) => {
    const origin = Component.getOrigin(component);
    if (!origin || !isUserComponent(componentType)) {
      // Early return if not a library component
      return;
    }

    const originPluginName = getPluginName(origin);
    if (originPluginName === 'Basic') {
      // There was a bug with Basic::LineBreak plugin components
      // being treated as library components. The bug has been fixed
      // but we still need to run a migration, so there are production sites
      // with LineBreak components with an origin. We should exclude them from here.
      // Migration ticket: https://webflow.atlassian.net/browse/SCALE-959
      return;
    }

    if (!pluginComponents.get(origin)) {
      // If we don't find a corresponding library Plugin Component
      // it means that the library User Component is orphaned.
      orphanedComponents[originPluginName] = [
        ...(orphanedComponents[originPluginName] || []),
        componentType,
      ];
    }
  });

  return orphanedComponents;
};

/**
 * Returns an updated componentMap where the orphaned components
 * have been either updated to the latest origin (given a pluginNameMap)
 * or detached from their origin.
 */
export const recoverOrphanedComponents = ({
  orphanedComponents,
  componentMap,
  pluginNameMap,
  plugins,
  replaceIfExists = false,
  updateComponentRenderFn,
}: {
  /**
   * A map of orphaned components by pluginName
   */
  orphanedComponents: OrphanedComponentsMap;
  /**
   * The current componentMap
   */
  componentMap: Component.ComponentMap;
  /**
   * A map of oldPluginName -> newPluginName
   * used to update orphaned components to the latest origin
   */
  pluginNameMap: {[pluginName: string]: string | null};
  /**
   * The current plugins
   */
  plugins: Plugins.PluginsType;
  /**
   * If true - if we find an existing user component on the expected origin
   * then we replace each instance of the orphaned component with the correct component
   *
   * Default: false
   */
  replaceIfExists?: boolean;
  /**
   * An optional updater function to update each component render
   */
  updateComponentRenderFn?: UpdateComponentRenderFnType;
}): {
  /**
   * The updated componentMap
   */
  componentMap: Component.ComponentMap;
  /**
   * Map of old ids to new ids
   */
  idMap: IdMap;
  /**
   * A list of new component origins that have been added/updated to the `componentMap`
   * and need to be processed on the caller side. We use this to lookup the component
   * dependencies in the elementPreset from the plugin.
   */
  newComponents: Qualified<Component.ComponentName>[];
  /**
   * A list of local components that have been detached from the origin plugin
   * and need to be processed on the caller side.
   */
  detachedComponents: Qualified<Component.ComponentName>[];
  /**
   * A map of local components that have been replaced with the updated version.
   */
  replacedComponents: Map<Component.ComponentName, Component.ComponentName>;
  /**
   * A set of all the element ids in the local components that have been removed
   */
  deletedElements: Set<string>;
} => {
  // Create a mutable copy of the componentMap that we'll use going forward
  const componentMap_mut = componentMap.asMutableCopy();
  const idMap_mut = new Map<string, string>();
  const pluginComponents = Plugins.getPluginComponents(plugins);

  // We keep a list of components are being added and detached
  const detachedComponents: Qualified<Component.ComponentName>[] = [];
  const replacedComponents: Map<
    Component.ComponentName,
    Component.ComponentName
  > = new Map();
  const deletedElements: Set<string> = new Set();
  let newComponents: Qualified<Component.ComponentName>[] = [];

  const componentsToCreateMap_mut: MutableComponentNameMap =
    new MutableQualifiedMap();
  const componentsToReplace_mut: MutableComponentNameMap =
    new MutableQualifiedMap();

  const _detachComponent = (
    componentType: Qualified<Component.ComponentName>,
    component: Component.ComponentType,
    additionalDescription?: string
  ) => {
    const displayName = Component.getDisplayName(component);
    const description = Component.getDescription(component) || '';

    const fields: Partial<Component.ComponentType> = {
      displayName: `${displayName} (Recovered)`,
    };
    if (additionalDescription) {
      const separator = description ? '\n\n' : '';
      fields.description = `${description}${separator}${additionalDescription}`;
    }

    const nextComponent = Component.update(component, fields);

    componentMap_mut.set_mut(
      componentType,
      detachComponentFromOrigin(
        nextComponent,
        componentMap_mut,
        updateComponentRenderFn
      )
    );

    detachedComponents.push(componentType);
  };

  /*
   * Iterate over each orphaned component:
   * - validate that it's a valid orphaned component
   * - if origin's plugin not found: detach component
   * - if origin's plugin component not found: detach component
   * - if updated origin's user component already exists: detach component
   * - if updated origin's user component doesn't exists: create new and override component
   */
  Object.values(orphanedComponents).forEach((orphanedComponentTypes) => {
    orphanedComponentTypes.forEach((orphanedComponentType) => {
      const orphanedComponent = componentMap_mut.get(orphanedComponentType);
      if (!orphanedComponent) {
        reportError(
          `[recoverComponentsWithOrphanedOrigin] Orphaned component ${orphanedComponentType} not found.`
        );
        return;
      }
      const orphanedComponentOrigin = Component.getOrigin(orphanedComponent);
      if (!orphanedComponentOrigin) {
        reportError(
          `[recoverComponentsWithOrphanedOrigin] Orphaned component ${orphanedComponentType} doesn't have an origin.`
        );
        return;
      }
      if (pluginComponents.get(orphanedComponentOrigin)) {
        reportError(
          `[recoverComponentsWithOrphanedOrigin] Component ${orphanedComponentType} is not orphaned.`
        );
        return;
      }

      const orphanedPluginName = getPluginName(orphanedComponentOrigin);
      const newPluginName = pluginNameMap[orphanedPluginName];

      if (!newPluginName) {
        // If we don't find a corresponding plugin
        // then we simply detach the component.
        // (this would be the case if the library was uninstalled)
        _detachComponent(
          orphanedComponentType,
          orphanedComponent,
          'Recovered: The library for this component was uninstalled.'
        );
        return;
      }

      const newOrigin = qualify(
        Plugins.name(newPluginName),
        Component.name(getName(orphanedComponentOrigin))
      );

      if (!componentMap.get(newOrigin)) {
        // If we don't find a corresponding plugin component
        // then we simply detach the component.
        // (this would be the case if the component doesn't exist
        // anymore on the latest installed library version)
        _detachComponent(
          orphanedComponentType,
          orphanedComponent,
          'Recovered: This component was removed from the installed library.'
        );
        return;
      }

      // Get the corresponding user component by origin
      const {componentId: userComponentType} = getComponentByOrigin(
        newOrigin,
        componentMap
      );

      if (userComponentType) {
        // If we find an existing user component with the same origin
        if (replaceIfExists) {
          // we replace the orphaned component instances with the existing component
          componentsToReplace_mut.set_mut(
            orphanedComponentType,
            userComponentType
          );
        } else {
          // we detach the orphaned component to avoid collisions
          _detachComponent(
            orphanedComponentType,
            orphanedComponent,
            'Recovered: A component with the same origin already exists.'
          );
        }
      } else {
        // If we don't find a corresponding user component with the
        // expected origin then we need to create a new one and we can override
        // the existing orphaned one
        componentsToCreateMap_mut.set_mut(newOrigin, orphanedComponentType);

        // We also delete the current orphaned component
        // This will be recreated later with the updated version
        componentMap_mut.delete_mut(orphanedComponentType);
      }
    });
  });

  // If there are any components to create we create them (and any possible nested component)
  if (componentsToCreateMap_mut.size() > 0) {
    // Build a componentOriginMap with all existing origins
    // + new components to be created
    const componentOriginMap = buildComponentOriginMap(componentMap_mut);
    const componentOriginMap_mut = componentOriginMap
      .union(componentsToCreateMap_mut)
      .asMutableCopy();

    const missingStyleVariants = new Map<
      string,
      {originType: Qualified<string>; pluginName: string}
    >();

    ({created: newComponents} = reduceComponentOriginMap(
      componentOriginMap_mut,
      componentMap_mut,
      idMap_mut,
      plugins,
      missingStyleVariants,
      updateComponentRenderFn
    ));

    updateMissingStyleVariants(
      componentOriginMap_mut,
      componentMap_mut,
      missingStyleVariants
    );
  }

  // If there are any orphaned components to replace
  if (componentsToReplace_mut.size() > 0) {
    // we iterate over all components are replace each instance
    componentMap_mut.forEach((component, componentType) => {
      const render = Component.getRender(component);
      const {elementExpression: nextRender, changed} =
        replaceUserComponentInstInExpression(render, componentsToReplace_mut);

      const withRender = changed
        ? Component.update(component, {render: nextRender})
        : component;
      const nextComponent = mapSlotDefaultLists(withRender, (list) => {
        const {elementExpression, changed: listChanged} =
          replaceUserComponentInstInExpression(list, componentsToReplace_mut);
        return listChanged ? elementExpression : list;
      });

      if (nextComponent !== component) {
        componentMap_mut.set_mut(componentType, nextComponent);
      }
    });

    componentsToReplace_mut.forEach((componentTypeTo, componentTypeFrom) => {
      // collect the id map (used on the caller side to replace instances in page dom nodes)
      replacedComponents.set(
        getName(componentTypeFrom),
        getName(componentTypeTo)
      );

      const replacedComponent = componentMap_mut.get(componentTypeFrom);
      // this should always exist
      if (replacedComponent) {
        // collect the ids of all elements in the component (used on the caller side)
        getElementIdsFromExpression(
          Component.getRender(replacedComponent)
        ).forEach((id) => deletedElements.add(id));
        getSlotDefaultLists(replacedComponent).forEach(([, list]) =>
          getElementIdsFromExpression(list).forEach((id) =>
            deletedElements.add(id)
          )
        );

        // remove the replaced component from the componentMap (now that each instance has been replaced)
        componentMap_mut.delete_mut(componentTypeFrom);
      }
    });
  }

  return {
    componentMap: componentMap_mut.asNonMutableCopy(),
    idMap: Object.fromEntries(idMap_mut.entries()),
    newComponents,
    detachedComponents,
    replacedComponents,
    deletedElements,
  };
};

const getElementIdsFromExpression = Expressions.everything(concat)(
  matchExpression({
    Element: ({id}) => {
      return Expressions.resume([id]);
    },
    _: constant(Expressions.resume([])),
  })
);

const getDisplayNameWithNoConflicts = (
  displayName: Component.ComponentType['displayName'],
  componentMap: Component.ComponentMap
) => {
  const otherNames: {[name: string]: string} = {};
  componentMap.forEach((component, componentName) => {
    const isConflictTarget =
      isUserComponent(componentName) &&
      Component.getOrigin(component) === undefined;
    if (isConflictTarget) {
      const name = Component.getDisplayName(component);
      otherNames[name] = name;
    }
  });

  return avoidDisplayNameConflict(ImmMap(otherNames))(displayName);
};

export const forTestSuite = {
  buildComponentOriginMap,
  replacePluginComponentInstInExpression,
  replaceUserComponentInstInExpression,
  reduceComponentOriginMap,
  detachComponentFromOrigin,
};
